import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { VersionProvider } from './contexts/VersionContext';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './pages/HomePage';
import InceptumPage from './pages/InceptumPage';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { gaEvent } from './analytics';
import './App.css';

const AboutUsPage = React.lazy(() => import('./pages/AboutUsPage'));
const Checkout = React.lazy(() => import('./pages/Checkout'));
const OrderConfirmation = React.lazy(() => import('./pages/OrderConfirmation'));
const Contact = React.lazy(() => import('./pages/Contact'));
const JournalIndex = React.lazy(() => import('./pages/JournalIndex'));
const JournalPost = React.lazy(() => import('./pages/JournalPost'));
const IpPolicy = React.lazy(() => import('./pages/IpPolicy'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const RefundAndCancellationPolicy = React.lazy(() => import('./pages/RefundAndCancellationPolicy'));
const ShippingPolicy = React.lazy(() => import('./pages/ShippingPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

const CHECKOUT_ENABLED = process.env.REACT_APP_ENABLE_CHECKOUT === 'true';

function App() {
  return (
    <ErrorBoundary>
      <VersionProvider>
        <BrowserRouter>
          <ScrollManager />
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/inceptum" element={<InceptumPage />} />
            <Route path="/about-us" element={<AboutUsPage />} />
            <Route
              path="/checkout"
              element={CHECKOUT_ENABLED ? <Checkout /> : <Navigate to="/" replace />}
            />
            <Route path="/contact" element={<Contact />} />
            <Route path="/journal" element={<PageWithChrome><JournalIndex /></PageWithChrome>} />
            <Route path="/journal/:slug" element={<PageWithChrome><JournalPost /></PageWithChrome>} />
            <Route path="/blog" element={<Navigate to="/journal" replace />} />
            <Route path="/blog/:slug" element={<LegacyJournalRedirect />} />
            <Route path="/order-confirmation" element={<OrderConfirmation />} />
            <Route path="/privacy-policy" element={<PageWithChrome><PrivacyPolicy /></PageWithChrome>} />
            <Route
              path="/refund-cancellation-policy"
              element={<PageWithChrome><RefundAndCancellationPolicy /></PageWithChrome>}
            />
            <Route path="/shipping" element={<PageWithChrome><ShippingPolicy /></PageWithChrome>} />
            <Route path="/ip-policy" element={<PageWithChrome><IpPolicy /></PageWithChrome>} />
            <Route path="/terms-of-service" element={<PageWithChrome><TermsOfService /></PageWithChrome>} />
            <Route path="*" element={<PageWithChrome><NotFound /></PageWithChrome>} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </VersionProvider>
    </ErrorBoundary>
  );
}

function PageWithChrome({ children }) {
  return (
    <>
      <Navbar />
      {children}
      <Footer />
    </>
  );
}

function ScrollManager() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const focusWaitlist = params.get('waitlist') === '1';

    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      if (focusWaitlist && (pathname === '/' || pathname === '/inceptum')) {
        const timer = window.setTimeout(() => {
          const input = document.getElementById('hero-waitlist-email');
          if (input && typeof input.focus === 'function') {
            input.focus({ preventScroll: true });
          }
        }, 500);
        return () => window.clearTimeout(timer);
      }
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [pathname, search, hash]);

  useEffect(() => {
    gaEvent('page_view', {
      page_path: `${pathname}${search}`,
      page_title: document.title,
    });
  }, [pathname, search]);

  return null;
}

function LegacyJournalRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/journal/${slug || ''}`} replace />;
}

export default App;
